import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X, Mail, Phone, Plus, Layers, Car, Users, Building, Utensils, Home } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import axios from 'axios';
import { SiteDataProvider, useSiteData } from '../context/SiteDataContext';

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const NYZA_LOGO_URL = "/images/nyza-logo.webp";

// Icon mapping for products
const iconMap = {
  'car': Car,
  'users': Users,
  'layers': Layers,
  'building': Building,
  'utensils': Utensils,
  'home': Home,
};

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 py-4 md:py-6 bg-gradient-to-b from-white/90 to-white/70 backdrop-blur-md">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="relative z-10">
            <img src={NYZA_LOGO_URL} alt="NYZA Studio" className="h-8 md:h-10" style={{ filter: 'brightness(0)' }} />
          </Link>
          
          <div className="hidden md:flex items-center bg-white/90 backdrop-blur-md rounded-full px-2 py-1.5 shadow-md border border-gray-200">
            <Link to="/" className="px-4 py-1.5 text-xs font-medium uppercase tracking-wide transition-all duration-200 rounded-full text-gray-600 hover:text-gray-900">HOME</Link>
            <Link to="/references" className="px-4 py-1.5 text-xs font-medium uppercase tracking-wide transition-all duration-200 rounded-full text-gray-600 hover:text-gray-900">REFERENCES</Link>
            <Link to="/products" className="px-4 py-1.5 text-xs font-medium uppercase tracking-wide transition-all duration-200 rounded-full text-gray-900 bg-white shadow-sm">PRODUCTS</Link>
            <Link to="/marketing" className="px-4 py-1.5 text-xs font-medium uppercase tracking-wide transition-all duration-200 rounded-full text-gray-600 hover:text-gray-900">MARKETING</Link>
          </div>
          
          <Link to="/#contact" className="hidden md:block bg-[#9A45FF] text-white text-xs font-medium uppercase tracking-wide px-5 py-2 rounded-full hover:bg-[#8035E8] transition-colors">CONTACT</Link>
          
          <button className="md:hidden p-2 text-gray-900" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        
        {isOpen && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="md:hidden mt-4 bg-white rounded-2xl shadow-lg border border-gray-100 p-4">
            <Link to="/" className="block py-2 text-sm text-gray-900 font-medium uppercase tracking-wide" onClick={() => setIsOpen(false)}>HOME</Link>
            <Link to="/references" className="block py-2 text-sm text-gray-900 font-medium uppercase tracking-wide" onClick={() => setIsOpen(false)}>REFERENCES</Link>
            <Link to="/products" className="block py-2 text-sm text-purple-600 font-medium uppercase tracking-wide" onClick={() => setIsOpen(false)}>PRODUCTS</Link>
            <Link to="/marketing" className="block py-2 text-sm text-gray-900 font-medium uppercase tracking-wide" onClick={() => setIsOpen(false)}>MARKETING</Link>
            <Link to="/#contact" className="block w-full text-center mt-3 bg-[#9A45FF] text-white text-sm font-medium uppercase tracking-wide px-4 py-2 rounded-full" onClick={() => setIsOpen(false)}>CONTACT</Link>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '', surname: '', email: '', phone: '',
    company: '', budget: '', message: '', privacy_accepted: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.privacy_accepted) { toast.error('Please accept the privacy policy'); return; }
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: `${formData.name} ${formData.surname}`,
        email: formData.email,
        company: formData.company,
        service: formData.budget || 'Product Inquiry',
        message: formData.message,
        privacy_accepted: formData.privacy_accepted
      });
      toast.success("Message sent successfully!");
      setFormData({ name: '', surname: '', email: '', phone: '', company: '', budget: '', message: '', privacy_accepted: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error sending message.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #F5F0FF 30%, #E8D4FF 70%, #D4B8FF 100%)' }}>
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-purple-300/30 blur-[150px]" />
      <div className="max-w-6xl mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} className="hidden lg:block">
            <img src="/images/contact.jpg" alt="Contact" className="rounded-2xl shadow-2xl w-full object-cover" loading="lazy" />
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">SHOOT US<br/><span className="bg-gradient-to-r from-purple-600 to-purple-400 bg-clip-text text-transparent">A MESSAGE</span></h2>
            <div className="flex flex-wrap gap-4 mb-6">
              <a href="mailto:workwith@nyza-studio.at" className="flex items-center gap-2 text-sm text-purple-600"><Mail className="w-4 h-4" />workwith@nyza-studio.at</a>
              <a href="https://wa.me/message/QRDZ3NTL345SA1" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-purple-600"><Phone className="w-4 h-4" />+43 660 5502578</a>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <input type="text" placeholder="First Name *" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="px-4 py-3 rounded-xl border border-gray-200 focus:border-purple-500 outline-none bg-white/80" />
                <input type="text" placeholder="Last Name *" value={formData.surname} onChange={(e) => setFormData({...formData, surname: e.target.value})} required className="px-4 py-3 rounded-xl border border-gray-200 focus:border-purple-500 outline-none bg-white/80" />
              </div>
              <input type="email" placeholder="Email *" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-purple-500 outline-none bg-white/80" />
              <textarea placeholder="Your Message *" value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} required rows={4} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-purple-500 outline-none resize-none bg-white/80" />
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={formData.privacy_accepted} onChange={(e) => setFormData({...formData, privacy_accepted: e.target.checked})} className="mt-1 w-4 h-4 rounded border-gray-300 text-purple-600" />
                <span className="text-xs text-gray-500">I accept the <Link to="/datenschutz" className="text-purple-600 hover:underline">Privacy Policy</Link> *</span>
              </label>
              <button type="submit" disabled={isSubmitting} className="w-full bg-gradient-to-r from-purple-600 to-purple-700 text-white py-4 rounded-xl font-semibold hover:from-purple-700 hover:to-purple-800 transition-all disabled:opacity-50">
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => (
  <footer className="py-16 px-4" style={{ background: 'linear-gradient(180deg, #D4B8FF 0%, #9A45FF 100%)' }}>
    <div className="max-w-6xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
        <div className="md:col-span-2">
          <img src={NYZA_LOGO_URL} alt="NYZA Studio" className="h-10 mb-4" style={{ filter: 'brightness(0) invert(1)' }} />
          <p className="text-white/80 text-sm max-w-md">Engineering digital excellence through curated software solutions and high-performance design systems.</p>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4 uppercase tracking-wide text-sm">Solutions</h4>
          <ul className="space-y-2 text-white/70 text-sm">
            <li><Link to="/marketing" className="hover:text-white transition-colors">Marketing</Link></li>
            <li><Link to="/products" className="hover:text-white transition-colors">Products</Link></li>
            <li><Link to="/schedule-demo" className="hover:text-white transition-colors">Book a Demo</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4 uppercase tracking-wide text-sm">Legal</h4>
          <ul className="space-y-2 text-white/70 text-sm">
            <li><Link to="/impressum" className="hover:text-white transition-colors">Imprint</Link></li>
            <li><Link to="/datenschutz" className="hover:text-white transition-colors">Privacy Policy</Link></li>
            <li><Link to="/agb" className="hover:text-white transition-colors">Terms</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/20 pt-8 text-center">
        <p className="text-white/60 text-sm">&copy; {new Date().getFullYear()} NYZA Studio. All rights reserved.</p>
      </div>
    </div>
  </footer>
);

// Product Card Component
const ProductCard = ({ product, isPopular }) => {
  const IconComponent = iconMap[product.icon] || Layers;
  
  return (
    <Link 
      to={`/products/${product.slug}`}
      className={`group p-1 rounded-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer ${
        isPopular 
          ? 'bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-300/50 shadow-[0_0_40px_-10px_rgba(128,32,228,0.3)]' 
          : 'bg-white border border-gray-200 hover:border-purple-200 hover:shadow-xl'
      }`}
    >
      <div className="p-8 flex flex-col h-full rounded-[calc(1rem-2px)]">
        <div className="flex justify-between items-start mb-6">
          {isPopular ? (
            <span className="text-[10px] font-bold uppercase tracking-widest bg-purple-600 px-3 py-1 rounded-full text-white">Most Popular</span>
          ) : (
            <span className="text-[10px] font-bold uppercase tracking-widest text-purple-600">{product.category}</span>
          )}
          <IconComponent className="w-6 h-6 text-purple-500" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-3">{product.name}</h3>
        <p className="text-gray-600 text-sm mb-8 leading-relaxed">{product.short_description}</p>
        <div className="mt-auto overflow-hidden rounded-xl border border-gray-100 bg-gray-50 aspect-video flex items-center justify-center">
          {product.image ? (
            <img 
              src={product.image} 
              alt={product.name} 
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center">
              <IconComponent className="w-16 h-16 text-purple-400" />
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

// Main Products Content
const ProductsContent = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      // Fallback data if API fails
      setProducts([
        { id: '1', name: 'CardealerOS', slug: 'cardealeros', category: 'Automotive', short_description: 'Revolutionize automotive retail with integrated inventory management and AI-driven lead tracking.', icon: 'car', is_popular: true },
        { id: '2', name: 'ClubOS', slug: 'clubos', category: 'Management', short_description: 'Exclusive member management for nightlife and private venues with tiered access control.', icon: 'users', is_popular: false },
        { id: '3', name: 'AgencyOS', slug: 'agencyos', category: 'Productivity', short_description: 'Scale your creative output with streamlined client portals and automated asset workflows.', icon: 'layers', is_popular: false },
        { id: '4', name: 'RestaurantOS', slug: 'restaurantos', category: 'Hospitality', short_description: 'End-to-end hospitality management from the kitchen line to the customer\'s plate.', icon: 'utensils', is_popular: false },
        { id: '5', name: 'HomeOS', slug: 'homeos', category: 'Real Estate', short_description: 'Smart property management for modern real estate portfolios and residential assets.', icon: 'home', is_popular: false },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f9f9f9]">
      <Navigation />
      <Toaster position="top-right" richColors />
      
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-8 pt-32 pb-20 text-center relative">
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-purple-500/10 blur-[120px] rounded-full pointer-events-none"></div>
        <span className="inline-block text-[10px] uppercase tracking-[0.2em] text-purple-600 mb-4 font-bold">The Ecosystem</span>
        <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none mb-6 text-gray-900">
          OUR PRODUCTS:<br/>
          <span className="bg-gradient-to-r from-purple-600 to-purple-400 bg-clip-text text-transparent uppercase">One Monthly Price, For All.</span>
        </h1>
        <p className="text-gray-500 text-sm tracking-widest uppercase">
          (PS: YOU CAN ALSO CUSTOMIZE OUR SOLUTIONS!)
        </p>
      </section>

      {/* Products Grid */}
      <section className="max-w-7xl mx-auto px-8 pb-24">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} isPopular={product.is_popular} />
            ))}
            
            {/* Custom Solution Card */}
            <Link 
              to="/schedule-demo"
              className="border-2 border-dashed border-gray-300 p-1 rounded-2xl group transition-all duration-500 hover:border-purple-400 cursor-pointer"
            >
              <div className="p-8 flex flex-col h-full items-center justify-center text-center min-h-[400px]">
                <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Plus className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Your OS?</h3>
                <p className="text-gray-600 text-sm">We build custom solutions tailored to your unique business logic.</p>
                <span className="mt-8 text-xs font-bold text-purple-600 tracking-widest uppercase">Start Building</span>
              </div>
            </Link>
          </div>
        )}
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-8 pb-24">
        <div className="bg-gradient-to-br from-purple-100 to-purple-50 p-1 rounded-2xl">
          <div className="bg-white rounded-[calc(1.5rem-4px)] p-12 text-center border border-purple-100 relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-purple-100/50 via-transparent to-transparent opacity-50"></div>
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">NEED SOMETHING TRULY UNIQUE?</h2>
              <p className="text-gray-600 max-w-2xl mx-auto mb-10 text-lg">Our solutions are modular by design. We can tailor any OS to fit your specific operational requirements or build a bespoke system from the ground up.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/schedule-demo" className="bg-purple-600 text-white px-10 py-4 rounded-full font-bold hover:bg-purple-700 transition-all">Talk to a Specialist</Link>
                <Link to="/#contact" className="text-gray-900 border border-gray-300 px-10 py-4 rounded-full font-bold hover:bg-gray-50 transition-all">Contact Us</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <ContactSection />
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default function Products() {
  return (
    <SiteDataProvider>
      <ProductsContent />
    </SiteDataProvider>
  );
}
